import React, {useContext, useEffect, useState} from 'react';
import './App.scss';
import AppContext, {SidebarType} from "./context/AppContext";
import SidebarWrapper from "./component/SidebarWrapper";
import VideoControlButtons from './component/video-chat/VideoControlButtons';
import ChatControlButton from "./component/general/controls/ChatControlButton";
import SVGToggleIcon from "./component/icons/SVGToggleIcon";
import {useTranslation} from "react-i18next";
import SVGIcon from "./component/icons/SVGIcon";
import NotificationContext, {NotificationList} from "./context/NotificationContext";
import VideoGrid from "./component/video-chat/VideoGrid";
import {Navigate, useNavigate} from "react-router-dom";
import useWebRtcClient from "./webrtc/WebRtcClient";
import InConfirm from "./component/general/InConfirm";
import InLayer from "./component/general/InLayer";
import {isDisconnectRejection} from "./ws/WsClient";
import {notifyWsFailure} from "./lib/notifyWsFailure";
import {isWaiting} from "./helper/presence";
import {ModeratorAdmitNotifier} from "./context/app/notifiers";
import {joinPath, readJoinTicket} from "./lib/joinTicket";

function App() {
  const webrtc = useWebRtcClient()

  const {t} = useTranslation()
  const navigate = useNavigate()

  const [ticket] = useState(readJoinTicket)

  const {
    initialized,
    checkedIn,
    userData,
    conferenceStatus,
    users,
    messages,
    sidebar, setSidebar,
    ws,
    wsConnected,
    exitReason,
  } = useContext(AppContext)

  const { clearNotifications, createNotification, hideNotification } = useContext(NotificationContext)

  const [ confirmAction, setConfirmAction ] = useState<'leave' | 'end' | null>(null)

  // i18n key group for the terminal exit notice below (kick / meeting ended / session expired).
  const exitReasonKey = exitReason === 'ended' ? 'sessionEnded'
    : exitReason === 'expired' ? 'sessionExpired'
    : 'removed'

  useEffect(() => {
    return () => {
      (async () => {
        await webrtc.disableTrack('camera', 'video')
        await webrtc.disableTrack('camera', 'audio')
        // RISKS.md/P1.3: the disconnect() below races these sends — a request caught mid-flight
        // now rejects (instead of hanging), which is fine during teardown but must not surface
        // as an unhandled rejection.
      })().catch(() => {})
      ws?.disconnect()
      // NotificationContext outlives this view, so a persistent toast follows into the green room.
      clearNotifications()
    }
  }, [])

  if (!ticket) {
    return (
      <InLayer title={t('chat.noSessionHere.title')} noButtons>
        {t('chat.noSessionHere.message')}
      </InLayer>
    )
  }

  // RISKS.md/P2.79: replace, never push: Back must not lead into a meeting room this tab has left.
  const greenRoom = joinPath(ticket)
  const toGreenRoom = () => navigate(greenRoom, { replace: true })

  if (!checkedIn) {
    return <Navigate to={greenRoom} replace/>
  }

  let style = 'inpriton'
  style = 'im'
  const classnames = ['App', style]

  // classnames.push('light')
  classnames.push('dark')

  const onSidebarChange = (e: any) => {
    e.preventDefault()
    setSidebar(e.target.value)
  }

  const isHost = () => userData?.isModerator

  // Ends the meeting for everyone without revoking anything, so every link stays valid and the
  // room can be reused (RISKS.md/P1.19). Not `ws.endSession()`, which does revoke.
  const endSession = async () => {
    try {
      await ws?.endConference()
    } catch (e) {
      // RISKS.md/P2.15: do NOT navigate on a refused end — the meeting goes on running for
      // everyone else, so leaving now would read as success.
      console.error('ending the session failed', e)
      notifyWsFailure({ createNotification, hideNotification }, e, {
        id: 'end-session-failed',
        message: t('chat.endSessionFailed'),
        ttl: 10000,
      })
      // Except with no socket: nothing can be ended over it, and we are leaving regardless.
      if (!isDisconnectRejection(e)) return
    }
    clearNotifications()
    toGreenRoom()
  }

  // Disconnects without revoking, so the same link can rejoin (RISKS.md/P1.19).
  const leaveSession = () => {
    clearNotifications()
    ws?.disconnect()
    toGreenRoom()
  }

  const onSidebarClick = (type: SidebarType) => {
    if (sidebar === type) {
      setSidebar(SidebarType.None)
    } else {
      setSidebar(type)
    }
  }

  const numOfWaitingParticipants = users.filter(isWaiting).length
  const numOfUnreadMessages = messages.filter(m => !m.read).length

  const admittedUsers = users.filter(u => conferenceStatus.admitted && u.admitted || (userData && userData.uuid === u.uuid))

  return (
    <div className={classnames.join(' ')}>
      {/* RISKS.md/P2.85: here, not in AppContext with the other notifiers, so each visit asks
          afresh over its own socket: a toast raised outside a visit admits over a closed one. */}
      <ModeratorAdmitNotifier/>
      {!initialized || !userData ? (
        <div>Loading...</div>
      ) : (
        <div id="main-wrap">
          <div>
            <div id="video-chat">
              {/*<VideoStream stream={stream} className="own-stream"/>*/}

              <VideoGrid users={admittedUsers}/>

              <div className="controls">
                <div className="left">
                  <div className="inner">
                    <div className="session-actions">
                      <button className="leave-session" onClick={() => setConfirmAction('leave')}>
                        {t('chat.leaveSession')}
                      </button>
                      {isHost() ? (
                        <button className="end-session" onClick={() => setConfirmAction('end')}>
                          {t('chat.endSession')}
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>

                <div className="center">
                  <div className="inner">
                    <VideoControlButtons/>
                  </div>
                </div>

                <div className="right">
                  <div className="inner">
                    {conferenceStatus.admitted ? (
                      <>
                        <ChatControlButton
                          onClick={() => onSidebarClick(SidebarType.Participants)}
                          active={sidebar === SidebarType.Participants}
                          title={t('videoControl.participants')}
                          activeTitle={t('videoControl.participants')}
                          counter={userData.isModerator ? numOfWaitingParticipants : 0}
                          icon={new SVGIcon('participants')}
                        />
                        <ChatControlButton
                          onClick={() => onSidebarClick(SidebarType.Chat)}
                          active={sidebar === SidebarType.Chat}
                          title={t('videoControl.messages')}
                          activeTitle={t('videoControl.messages')}
                          counter={numOfUnreadMessages}
                          icon={new SVGIcon('chat')}
                        />
                        <ChatControlButton
                          onClick={() => null}
                          active={false}
                          title={t('videoControl.screenShare.enable')}
                          activeTitle={t('videoControl.screenShare.disable')}
                          icon={new SVGToggleIcon('screen-share', 'screen-share-off')}
                        />
                      </>
                    ) : null}
                    <ChatControlButton
                      onClick={() => onSidebarClick(SidebarType.DeviceSettings)}
                      active={sidebar === SidebarType.DeviceSettings}
                      title={t('videoControl.settings')}
                      activeTitle={t('videoControl.settings')}
                      icon={new SVGIcon('settings')}
                    />
                  </div>
                </div>
              </div>
              <NotificationList/>
            </div>

            <select value={sidebar} onChange={e => onSidebarChange(e)}
                    style={{position: 'fixed', left: '10px', top: '10px'}}>
              <option value={SidebarType.None}>{SidebarType.None}</option>
              <option value={SidebarType.Participants}>{SidebarType.Participants}</option>
              <option value={SidebarType.Chat}>{SidebarType.Chat}</option>
              <option value={SidebarType.DeviceSettings}>{SidebarType.DeviceSettings}</option>
            </select>
          </div>

          <SidebarWrapper/>
        </div>
      )}
      {confirmAction ? (
        <InConfirm
          title={t(confirmAction === 'end' ? 'chat.confirm.closeChat.title' : 'chat.confirm.leaveSession.title')}
          message={t(confirmAction === 'end' ? 'chat.confirm.closeChat.message' : 'chat.confirm.leaveSession.message')}
          // RISKS.md/P2.15: close the dialog BEFORE acting. A refused "End session" stays in the
          // meeting, and a dialog left open sits on top of the button that is the retry.
          onConfirm={() => {
            const action = confirmAction === 'end' ? endSession : leaveSession
            setConfirmAction(null)
            void action()
          }}
          onCancel={() => setConfirmAction(null)}
        />
      ) : null}

      {/* RISKS.md/P1.19: dropped by a moderator kick (revoked) or "end for everyone" (rejoinable).
          Show a notice with an OK that returns to the green room (an ended-session user can rejoin
          there; a kicked user sees "link no longer valid"), NOT the misleading "reconnecting"
          overlay — we are intentionally disconnected.
          RISKS.md/P1.20 adds 'expired': silent refresh gave up (the session can no longer be
          renewed), so the session is dead even though nobody kicked us. Same notice pattern —
          rejoining from the green room is the way back. */}
      {exitReason ? (
        <InLayer
          title={t(`chat.${exitReasonKey}.title`)}
          onConfirm={toGreenRoom}
        >
          {t(`chat.${exitReasonKey}.message`)}
        </InLayer>
      ) : !wsConnected ? (
        <InLayer
          title={'Server-Verbindung unterbrochen'}
          noButtons
        >
          Die Verbindung wird wiederhergestellt...
        </InLayer>
      ) : null}
    </div>
  );
}

export default App;